// Spherical k-means clustering — unified core
//
// Used by: clustering.ts (content clusters), seeding.ts (seeding story selection),
// and Cloud Run /clustering endpoint (heavy computation offloaded from Edge).
//
// Algorithm: k-means++ initialization (distance²-weighted probabilistic seeding)
// followed by iterative assignment/update with L2-normalized centroids.
//
// Types are defined here (not in types/) so the Supabase edge runtime
// can mount a single file. types/ re-exports for Node.js.

// --- Types ---

/** Result of running k-means clustering */
export interface KMeansResult {
  /** Centroid vectors, one per cluster (index 0..k-1) */
  centroids: number[][];
  /** Cluster assignment for each input item (index into centroids) */
  assignments: number[];
  /** Number of items in each cluster */
  clusterSizes: number[];
}

// --- Constants ---

const KMEANS_MAX_ITERATIONS = 20;
const KMEANS_PRNG_SEED = 42;

// --- Public API ---

/**
 * Run spherical k-means clustering on a set of embeddings.
 *
 * Algorithm:
 * 1. Initialize k centroids via k-means++ (distance²-weighted probabilistic seeding).
 *    First centroid = most central point in a sample; remaining centroids are sampled
 *    with probability proportional to d² from nearest existing centroid (seeded PRNG).
 * 2. Iterate: assign each item to nearest centroid, recompute centroids as
 *    L2-normalized mean of cluster members.
 * 3. Converge when assignments stabilize or max iterations reached.
 *
 * Complexity: O(seedSampleSize²·D) for init seed + O(n·k·D·iterations) for iterations.
 */
export function runKMeans(
  embeddings: number[][],
  k: number,
  seedSampleSize: number = 2000,
): KMeansResult {
  if (embeddings.length === 0) {
    return { centroids: [], assignments: [], clusterSizes: [] };
  }

  const effectiveK = Math.min(k, embeddings.length);
  const dim = embeddings[0]!.length;

  // Step 1: Initialize centroids via k-means++
  const initIndices = initCentroidsKMeansPlusPlus(embeddings, effectiveK, seedSampleSize);
  const centroids = initIndices.map(idx => [...embeddings[idx]!]);

  // Step 2: Iterate assign + update until convergence
  let assignments = assignClusters(embeddings, centroids);

  for (let iter = 0; iter < KMEANS_MAX_ITERATIONS; iter++) {
    // Update centroids: mean of members, then L2-normalize
    for (let c = 0; c < effectiveK; c++) {
      const members: number[] = [];
      for (let i = 0; i < assignments.length; i++) {
        if (assignments[i] === c) members.push(i);
      }

      if (members.length === 0) {
        // Empty cluster: re-seed with the point farthest from any centroid
        centroids[c] = [...embeddings[findFarthestPoint(embeddings, centroids)]!];
      } else {
        centroids[c] = computeMeanCentroid(embeddings, members, dim);
      }
    }

    // Reassign and check convergence
    const newAssignments = assignClusters(embeddings, centroids);
    let changed = false;
    for (let i = 0; i < newAssignments.length; i++) {
      if (newAssignments[i] !== assignments[i]) { changed = true; break; }
    }
    assignments = newAssignments;
    if (!changed) break;
  }

  // Compute cluster sizes
  const clusterSizes = new Array<number>(effectiveK).fill(0);
  for (const a of assignments) {
    clusterSizes[a]! += 1;
  }

  return { centroids, assignments, clusterSizes };
}

// --- Vector math ---

/** Cosine similarity between two vectors. Returns 0 if either has zero magnitude. */
export function cosineSimilarity(a: number[], b: number[]): number {
  let dot = 0;
  let magA = 0;
  let magB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i]! * b[i]!;
    magA += a[i]! * a[i]!;
    magB += b[i]! * b[i]!;
  }
  const denom = Math.sqrt(magA) * Math.sqrt(magB);
  if (denom === 0) return 0;
  return dot / denom;
}

/** L2-normalize a vector. Returns the original if magnitude is 0. */
export function normalize(vec: number[]): number[] {
  const magnitude = Math.sqrt(vec.reduce((sum, v) => sum + v * v, 0));
  if (magnitude === 0) return vec;
  return vec.map(v => v / magnitude);
}

// --- Coverage-first selection with medoid snap (Issues #682, #759) ---
//
// Used by seeding story selection. Unlike k-means — which mirrors corpus
// density and so over-represents the majority topic — this flow splits the
// two concerns (Issue #759):
//
//   1. COVERAGE decides WHERE the k ballot seats are: farthest-first traversal
//      over the FULL pool maximizes the minimum pairwise distance among seats,
//      so a small distinct topic region always earns a seat by its
//      distinctness, not its size (Issue #682).
//   2. DENSITY decides WHO sits in each seat: each seat snaps to the medoid of
//      its Voronoi cell, so a fringe outlier near a region is never the final
//      representative when a denser cell-mate exists (preserves the #688
//      anti-outlier goal without erasing sparse-but-real regions).
//
// Genre-agnostic: operates purely on embeddings, where topics live.

/**
 * Index of the medoid — the most central embedding (highest average cosine
 * similarity to every other point). Deterministic anchor for farthest-first
 * traversal. Returns -1 for an empty set.
 *
 * Computed in O(n·D): for cosine, argmax over p of mean_j cos(p, x_j) equals
 * argmax over p of cos(p, Σ_j x̂_j), where x̂ is the unit-normalized embedding.
 * So the medoid is simply the point most aligned with the mean direction — no
 * O(n²) pairwise pass needed. Ties resolve to the lowest index.
 */
export function medoidIndex(embeddings: number[][]): number {
  if (embeddings.length === 0) return -1;
  return medoidIndexOf(embeddings, Array.from({ length: embeddings.length }, (_, i) => i));
}

/**
 * Medoid restricted to a subset of indices — the candidate most aligned with
 * the mean direction of that subset. Used by the coverage-first medoid snap
 * (Issue #759) to pick each seat's final representative from its Voronoi cell.
 * Returns -1 for an empty subset; ties resolve to the lowest index.
 */
export function medoidIndexOf(embeddings: number[][], indices: number[]): number {
  const m = indices.length;
  if (m === 0) return -1;
  if (m === 1) return indices[0]!;

  const dim = embeddings[indices[0]!]!.length;
  const meanDir = new Array<number>(dim).fill(0);
  for (const i of indices) {
    const unit = normalize(embeddings[i]!);
    for (let d = 0; d < dim; d++) meanDir[d]! += unit[d]!;
  }

  let bestIdx = indices[0]!;
  let bestSim = -Infinity;
  for (const i of indices) {
    const sim = cosineSimilarity(embeddings[i]!, meanDir);
    if (sim > bestSim) { bestSim = sim; bestIdx = i; }
  }
  return bestIdx;
}

/**
 * Farthest-first traversal (Gonzalez greedy k-center). Returns up to k indices
 * in coverage order: each next index is the point whose nearest already-selected
 * seed is the LEAST similar (i.e. farthest in cosine distance). This is a
 * 2-approximation for the max-min objective, so the chosen set spans the space
 * rather than concentrating on its densest region.
 *
 * The first seed is the medoid (most central point) unless startIndex is given.
 * Runs over the FULL pool — coverage decides seat placement with no density
 * gate (Issue #759); the medoid snap in `coverageMedoidSelect` handles
 * outliers AFTER all seats are placed.
 *
 * Deterministic — no PRNG; ties resolve to the lowest index. O(n·k·D).
 */
export function farthestFirstOrder(
  embeddings: number[][],
  k: number,
  startIndex?: number,
): number[] {
  const n = embeddings.length;
  if (n === 0 || k <= 0) return [];
  const target = Math.min(k, n);

  // First seed: an explicit, in-bounds startIndex; otherwise the pool medoid.
  const startEligible = startIndex !== undefined && startIndex >= 0 && startIndex < n;
  const first = startEligible ? startIndex! : medoidIndex(embeddings);
  const selected: number[] = [first];
  const isSelected = new Set<number>([first]);

  // nearestSim[i] = max cosine similarity of point i to any selected seed.
  // A point is "far" from the selected set when its nearestSim is low.
  const nearestSim = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    nearestSim[i] = cosineSimilarity(embeddings[i]!, embeddings[first]!);
  }

  while (selected.length < target) {
    let nextIdx = -1;
    let lowestSim = Infinity;
    for (let i = 0; i < n; i++) {
      if (isSelected.has(i)) continue;
      if (nearestSim[i]! < lowestSim) { lowestSim = nearestSim[i]!; nextIdx = i; }
    }
    if (nextIdx === -1) break;

    selected.push(nextIdx);
    isSelected.add(nextIdx);

    // Update each point's nearest-seed similarity with the new seed.
    for (let i = 0; i < n; i++) {
      if (isSelected.has(i)) continue;
      const sim = cosineSimilarity(embeddings[i]!, embeddings[nextIdx]!);
      if (sim > nearestSim[i]!) nearestSim[i] = sim;
    }
  }
  return selected;
}

/**
 * Result of coverage-first selection with medoid snap (Issue #759). All four
 * arrays are parallel: `representatives[p]` is the final story index for seat
 * p (in farthest-first traversal order), `clusterSizes[p]` its Voronoi share
 * of the pool, `cellMeans[p]` the seat's region anchor, and `assignments[i]`
 * maps pool index i to a seat position.
 */
export interface CoverageMedoidSelection {
  representatives: number[]; // final story index per seat, in traversal order
  assignments: number[];     // final Voronoi assignment of full pool to representatives
  clusterSizes: number[];    // parallel to representatives
  cellMeans: number[][];     // normalized mean of each FINAL cell — the seat's region anchor (#763)
}

/**
 * Coverage-first selection with medoid snap and per-show cap (Issue #759).
 *
 * 1. SEATS — coverage decides WHERE representation lives: farthest-first over
 *    the FULL pool (no density gate, no mode seeding). Snapping happens only
 *    AFTER all seats are placed so the traversal geometry is undisturbed.
 * 2. CELLS — Voronoi-assign the full pool to the seats.
 * 3. SNAP  — density decides WHO represents each seat: in traversal order,
 *    each seat takes the most central member of its cell (the cell medoid).
 *    When `showKeys` is provided, the cap skips members whose show already
 *    holds a seat and takes the next-most-central member from an unused show;
 *    if the ENTIRE cell belongs to already-used shows, the most central member
 *    wins anyway (duplicate allowed — a seat is never dropped).
 * 4. METRICS — re-assign the full pool to the FINAL representatives so
 *    clusterSizes reflect what users actually see, and compute each final
 *    cell's normalized mean. The mean is the seat's region ANCHOR (#763):
 *    votes contribute it, while the medoid story is only the displayed face —
 *    so swapping the face never moves what a vote means.
 *
 * Deterministic; ties resolve to the lowest index. O(n·k·D).
 */
export function coverageMedoidSelect(
  embeddings: number[][],
  k: number,
  showKeys?: (string | null | undefined)[],
): CoverageMedoidSelection {
  if (embeddings.length === 0 || k <= 0) {
    return { representatives: [], assignments: [], clusterSizes: [], cellMeans: [] };
  }

  // Step 1 — seats: farthest-first over the full pool (k capped internally).
  const seats = farthestFirstOrder(embeddings, k);

  // Step 2 — cells: Voronoi partition of the full pool around the seats.
  const cellOf = assignToSeeds(embeddings, seats);

  // Step 3 — snap each seat to its cell medoid, applying the per-show cap.
  const representatives: number[] = [];
  const usedShows = new Set<string>();
  for (let p = 0; p < seats.length; p++) {
    const members: number[] = [];
    for (let i = 0; i < cellOf.length; i++) {
      if (cellOf[i] === p) members.push(i);
    }
    // A seat is always a member of its own cell; the empty-cell fallback only
    // triggers for degenerate duplicate-embedding pools.
    const ordered = members.length > 0 ? centralityOrderOf(embeddings, members) : [seats[p]!];

    // Most central member whose show is unused; cap applies only when showKeys
    // is provided AND the member's key is a non-empty string. Fall back to the
    // plain medoid when every member's show is taken (duplicate allowed).
    let rep = ordered[0]!;
    if (showKeys) {
      const eligible = ordered.find(m => {
        const key = showKeys[m];
        return !(typeof key === 'string' && key.length > 0 && usedShows.has(key));
      });
      if (eligible !== undefined) rep = eligible;
    }
    representatives.push(rep);

    const repKey = showKeys?.[rep];
    if (typeof repKey === 'string' && repKey.length > 0) usedShows.add(repKey);
  }

  // Step 4 — final metrics over the snapped representatives.
  const assignments = assignToSeeds(embeddings, representatives);
  const clusterSizes = new Array<number>(representatives.length).fill(0);
  for (const pos of assignments) clusterSizes[pos]! += 1;

  // Cell means over the FINAL assignments so anchors match the cells users
  // see (same partition as clusterSizes). Mean of unit-normalized members,
  // re-normalized; a degenerate (zero) mean falls back to the representative's
  // own embedding so the anchor is never a null vector.
  const dim = embeddings[0]!.length;
  const sums = representatives.map(() => new Array<number>(dim).fill(0));
  for (let i = 0; i < assignments.length; i++) {
    const unit = normalize(embeddings[i]!);
    const sum = sums[assignments[i]!]!;
    for (let d = 0; d < dim; d++) sum[d]! += unit[d]!;
  }
  const cellMeans = sums.map((sum, p) => {
    const mag = Math.sqrt(sum.reduce((acc, x) => acc + x * x, 0));
    return mag < 1e-10 ? normalize(embeddings[representatives[p]!]!) : sum.map(x => x / mag);
  });

  return { representatives, assignments, clusterSizes, cellMeans };
}

/**
 * Order a subset of indices by centrality: cosine similarity to the subset's
 * mean direction (sum of unit-normalized members — the same math as
 * `medoidIndexOf`), descending; ties resolve to the lowest index. The first
 * element is therefore the subset's medoid.
 */
function centralityOrderOf(embeddings: number[][], indices: number[]): number[] {
  if (indices.length <= 1) return [...indices];

  const dim = embeddings[indices[0]!]!.length;
  const meanDir = new Array<number>(dim).fill(0);
  for (const i of indices) {
    const unit = normalize(embeddings[i]!);
    for (let d = 0; d < dim; d++) meanDir[d]! += unit[d]!;
  }

  return indices
    .map(i => ({ i, sim: cosineSimilarity(embeddings[i]!, meanDir) }))
    .sort((a, b) => b.sim - a.sim || a.i - b.i)
    .map(x => x.i);
}

/**
 * Assign each embedding to its nearest seed (by cosine similarity). Returns an
 * array parallel to `embeddings`, each value a position 0..seedIndices.length-1.
 * Ties resolve to the lowest seed position. The Voronoi partition this induces
 * gives each seed's "represented share" of the pool.
 */
export function assignToSeeds(embeddings: number[][], seedIndices: number[]): number[] {
  return embeddings.map(emb => {
    let bestPos = 0;
    let bestSim = -Infinity;
    for (let s = 0; s < seedIndices.length; s++) {
      const sim = cosineSimilarity(emb, embeddings[seedIndices[s]!]!);
      if (sim > bestSim) { bestSim = sim; bestPos = s; }
    }
    return bestPos;
  });
}

/** Minimum pairwise cosine similarity among the given embeddings (null if < 2). */
export function minPairwiseCosineSimilarity(embeddings: number[][]): number | null {
  if (embeddings.length < 2) return null;
  let minSim = Infinity;
  for (let i = 0; i < embeddings.length; i++) {
    for (let j = i + 1; j < embeddings.length; j++) {
      const sim = cosineSimilarity(embeddings[i]!, embeddings[j]!);
      if (sim < minSim) minSim = sim;
    }
  }
  return minSim;
}

// --- K-means internals (exported for testing only) ---

/** Assign each embedding to the nearest centroid by cosine similarity. */
function assignClusters(embeddings: number[][], centroids: number[][]): number[] {
  return embeddings.map(emb => {
    let bestCluster = 0;
    let bestSim = -Infinity;
    for (let c = 0; c < centroids.length; c++) {
      const sim = cosineSimilarity(emb, centroids[c]!);
      if (sim > bestSim) {
        bestSim = sim;
        bestCluster = c;
      }
    }
    return bestCluster;
  });
}

/** Compute L2-normalized mean centroid from cluster members. */
function computeMeanCentroid(embeddings: number[][], memberIndices: number[], dim: number): number[] {
  const mean = new Array<number>(dim).fill(0);
  for (const idx of memberIndices) {
    for (let d = 0; d < dim; d++) {
      mean[d]! += embeddings[idx]![d]!;
    }
  }
  for (let d = 0; d < dim; d++) {
    mean[d]! /= memberIndices.length;
  }
  return normalize(mean);
}

/**
 * Initialize k centroids using k-means++ (deterministic variant with seeded PRNG).
 *
 * 1. Find the most central point in a sample as the first centroid.
 * 2. For each remaining centroid: sample with probability proportional to d²
 *    from nearest existing centroid.
 */
function initCentroidsKMeansPlusPlus(embeddings: number[][], k: number, sampleSize: number): number[] {
  const n = embeddings.length;
  const rng = mulberry32(KMEANS_PRNG_SEED);

  // Find seed: most central embedding in a deterministic sample
  const sampleIndices = n > sampleSize
    ? deterministicSampleIndices(n, sampleSize)
    : Array.from({ length: n }, (_, i) => i);

  let bestSeedSamplePos = 0;
  let bestAvgSim = -Infinity;

  for (let i = 0; i < sampleIndices.length; i++) {
    let simSum = 0;
    for (let j = 0; j < sampleIndices.length; j++) {
      if (i !== j) {
        simSum += cosineSimilarity(embeddings[sampleIndices[i]!]!, embeddings[sampleIndices[j]!]!);
      }
    }
    const avgSim = simSum / Math.max(1, sampleIndices.length - 1);
    if (avgSim > bestAvgSim) {
      bestAvgSim = avgSim;
      bestSeedSamplePos = i;
    }
  }

  const selected: number[] = [sampleIndices[bestSeedSamplePos]!];
  const isSelected = new Set<number>([selected[0]!]);

  // K-means++: sample each subsequent centroid with probability ∝ d²
  while (selected.length < k) {
    const weights = new Array<number>(n);
    let totalWeight = 0;

    for (let i = 0; i < n; i++) {
      if (isSelected.has(i)) {
        weights[i] = 0;
        continue;
      }
      let minDist = Infinity;
      for (const selIdx of selected) {
        const dist = 1 - cosineSimilarity(embeddings[i]!, embeddings[selIdx]!);
        if (dist < minDist) minDist = dist;
      }
      weights[i] = minDist * minDist;
      totalWeight += weights[i]!;
    }

    if (totalWeight === 0) break;

    const threshold = rng() * totalWeight;
    let cumulative = 0;
    let nextIdx = -1;

    for (let i = 0; i < n; i++) {
      cumulative += weights[i]!;
      if (cumulative >= threshold) {
        nextIdx = i;
        break;
      }
    }

    if (nextIdx === -1) nextIdx = n - 1;
    selected.push(nextIdx);
    isSelected.add(nextIdx);
  }

  return selected;
}

/** Find the point farthest from any centroid (for re-seeding empty clusters). */
function findFarthestPoint(embeddings: number[][], centroids: number[][]): number {
  let bestIdx = 0;
  let bestMinDist = -Infinity;

  for (let i = 0; i < embeddings.length; i++) {
    let minDist = Infinity;
    for (const centroid of centroids) {
      const dist = 1 - cosineSimilarity(embeddings[i]!, centroid);
      if (dist < minDist) minDist = dist;
    }
    if (minDist > bestMinDist) {
      bestMinDist = minDist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Seeded PRNG (mulberry32). Returns a function that produces deterministic
 * pseudo-random numbers in [0, 1) for the given seed. Full 2^32 period.
 */
function mulberry32(seed: number): () => number {
  return () => {
    // eslint-disable-next-line no-bitwise, no-multi-assign, no-param-reassign
    let t = seed += 0x6D2B79F5;
    // eslint-disable-next-line no-bitwise
    t = Math.imul(t ^ (t >>> 15), t | 1);
    // eslint-disable-next-line no-bitwise
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    // eslint-disable-next-line no-bitwise
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic evenly-spaced index selection. */
function deterministicSampleIndices(length: number, n: number): number[] {
  if (n >= length) return Array.from({ length }, (_, i) => i);
  const step = length / n;
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    result.push(Math.floor(i * step));
  }
  return result;
}
